import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Plus, Tag, Trash2, Pencil, Search, Merge, Check, X,
  Loader2, Save, Trophy, Gamepad2, Globe, Building2, Wrench, Shield,
  Layers, ChevronUp, ChevronDown, Flag,
} from "lucide-react";

const API = `${getApiUrl()}/api`;

const COLOR_OPTIONS = [
  "#EF4444", "#F97316", "#F59E0B", "#84CC16", "#22C55E", "#10B981",
  "#14B8A6", "#06B6D4", "#3B82F6", "#6366F1", "#8B5CF6", "#A855F7",
  "#D946EF", "#EC4899", "#F43F5E", "#78716C",
];

// Render a single tag card (used inside TagGrid groups)
function TagCard({ tag, count, selected, mergeMode, onClick, openEdit, setDeleteTarget, showDescription }) {
  return (
    <TooltipProvider key={tag.id}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`group flex items-center gap-3 p-3 rounded-lg border transition-all ${mergeMode ? (selected ? "border-amber-500 bg-amber-500/15 cursor-pointer" : "border-white/10 bg-white/5 hover:border-amber-500/50 cursor-pointer") : "border-white/5 bg-white/[0.02] hover:border-white/10"}`}
            onClick={onClick}
            data-testid={`tag-${tag.id}`}
          >
            {mergeMode && (
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? "border-amber-500 bg-amber-500" : "border-white/20"}`}>
                {selected && <Check className="w-3 h-3 text-black" />}
              </div>
            )}
            {tag.color && <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />}
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{tag.name}</p>
              {showDescription && tag.description && (
                <p className="text-white/30 text-xs truncate">{tag.description}</p>
              )}
            </div>
            <span className="text-white/30 text-xs shrink-0">{count}</span>
            {!mergeMode && (
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEdit(tag)} className="p-1 rounded hover:bg-white/10" data-testid={`edit-tag-${tag.id}`}><Pencil className="w-3 h-3 text-white/50" /></button>
                <button onClick={() => setDeleteTarget(tag)} className="p-1 rounded hover:bg-red-500/20" data-testid={`delete-tag-${tag.id}`}><Trash2 className="w-3 h-3 text-red-400/50" /></button>
              </div>
            )}
          </div>
        </TooltipTrigger>
        {tag.description && (
          <TooltipContent side="top" className="bg-black border-white/10 text-white max-w-xs">
            <p className="text-xs">{tag.description}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

// Reusable tag grid component — when `groups` is non-empty, tags are organized
// by their groupId (admin convenience). Ungrouped tags sit at the bottom.
function TagGrid({ tags, usage, search, mergeMode, selectedForMerge, toggleMergeSelection, openEdit, setDeleteTarget, showDescription, groups = [] }) {
  const filtered = tags.filter(t => t.name?.toLowerCase().includes(search.toLowerCase()));
  if (filtered.length === 0) return <p className="text-center text-white/30 py-8">No tags found.</p>;

  // Bucket tags by groupId. Tags whose group no longer exists fall to "Ungrouped".
  const groupIds = new Set(groups.map(g => g.id));
  const byGroup = new Map(groups.map(g => [g.id, []]));
  const ungrouped = [];
  filtered.forEach(t => {
    if (t.groupId && groupIds.has(t.groupId)) {
      byGroup.get(t.groupId).push(t);
    } else {
      ungrouped.push(t);
    }
  });

  const renderCard = (tag) => {
    const count = usage[tag.id] || 0;
    const selected = selectedForMerge.includes(tag.id);
    return (
      <TagCard
        key={tag.id}
        tag={tag}
        count={count}
        selected={selected}
        mergeMode={mergeMode}
        onClick={mergeMode ? () => toggleMergeSelection(tag.id) : undefined}
        openEdit={openEdit}
        setDeleteTarget={setDeleteTarget}
        showDescription={showDescription}
      />
    );
  };

  // No groups configured → render the original flat grid for parity.
  if (groups.length === 0) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {filtered.map(renderCard)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map(g => {
        const items = byGroup.get(g.id) || [];
        if (items.length === 0) return null;
        return (
          <div key={g.id} data-testid={`tag-group-section-${g.id}`}>
            <div className="flex items-center gap-2 mb-2 px-1">
              <Layers className="w-3.5 h-3.5 text-white/40" />
              <h3 className="text-xs font-semibold text-white/60 uppercase tracking-wider">{g.name}</h3>
              <span className="text-[10px] text-white/30">({items.length})</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {items.map(renderCard)}
            </div>
          </div>
        );
      })}
      {ungrouped.length > 0 && (
        <div data-testid="tag-group-section-ungrouped">
          <div className="flex items-center gap-2 mb-2 px-1">
            <Layers className="w-3.5 h-3.5 text-white/30" />
            <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Ungrouped</h3>
            <span className="text-[10px] text-white/30">({ungrouped.length})</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {ungrouped.map(renderCard)}
          </div>
        </div>
      )}
    </div>
  );
}

// Metadata grid (simpler - no color, shows system for ratings)
function MetadataGrid({ items, usage, search, openEdit, setDeleteTarget }) {
  const filtered = items.filter(t => t.name?.toLowerCase().includes(search.toLowerCase()));
  if (filtered.length === 0) return <p className="text-center text-white/30 py-8">No items found.</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {filtered.map(item => {
        const count = usage[item.id] || 0;
        return (
          <TooltipProvider key={item.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="group flex items-center gap-3 p-3 rounded-lg border border-white/5 bg-white/[0.02] hover:border-white/10 transition-all" data-testid={`meta-${item.id}`}>
                  {item.code && (
                    <span className="shrink-0 px-2 py-0.5 rounded bg-white/10 text-white/80 text-xs font-mono font-semibold">{item.code}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{item.name}</p>
                    {item.system && <p className="text-white/30 text-xs">{item.system}</p>}
                    {item.description && !item.system && <p className="text-white/30 text-xs truncate">{item.description}</p>}
                  </div>
                  <span className="text-white/30 text-xs shrink-0">{count} game{count !== 1 ? "s" : ""}</span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEdit(item)} className="p-1 rounded hover:bg-white/10" data-testid={`edit-meta-${item.id}`}><Pencil className="w-3 h-3 text-white/50" /></button>
                    <button onClick={() => setDeleteTarget(item)} className="p-1 rounded hover:bg-red-500/20" data-testid={`delete-meta-${item.id}`}><Trash2 className="w-3 h-3 text-red-400/50" /></button>
                  </div>
                </div>
              </TooltipTrigger>
              {item.description && (
                <TooltipContent side="top" className="bg-black border-white/10 text-white max-w-xs">
                  <p className="text-xs">{item.description}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}

export default function MasterDatabaseTags() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("achievement");

  // Achievement tags
  const [achTags, setAchTags] = useState([]);
  const [achUsage, setAchUsage] = useState({});
  const [achLoading, setAchLoading] = useState(true);

  // Game tags
  const [gameTags, setGameTags] = useState([]);
  const [gameUsage, setGameUsage] = useState({});
  const [gameLoading, setGameLoading] = useState(true);

  // Unified tags
  const [unifiedTags, setUnifiedTags] = useState([]);
  const [unifiedUsage, setUnifiedUsage] = useState({});
  const [unifiedLoading, setUnifiedLoading] = useState(true);

  // Genres (functionally a tag — same UI pattern). Moved from Configuration.
  const [genreItems, setGenreItems] = useState([]);
  const [genreUsage, setGenreUsage] = useState({});
  const [genreLoading, setGenreLoading] = useState(true);

  // Tag groups (admin-only). Separate buckets for achievement vs unified tags.
  const [achGroups, setAchGroups] = useState([]);
  const [unifiedGroups, setUnifiedGroups] = useState([]);
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [groupForm, setGroupForm] = useState({ id: null, name: "" });
  // Move-to-group bulk action state
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveTargetGroupId, setMoveTargetGroupId] = useState("__none__");
  const [moving, setMoving] = useState(false);

  // Metadata: Publishers, Developers, Ratings
  const [publishers, setPublishers] = useState([]);
  const [pubUsage, setPubUsage] = useState({});
  const [pubLoading, setPubLoading] = useState(true);

  const [developers, setDevelopers] = useState([]);
  const [devUsage, setDevUsage] = useState({});
  const [devLoading, setDevLoading] = useState(true);

  const [ratings, setRatings] = useState([]);
  const [ratingUsage, setRatingUsage] = useState({});
  const [ratingLoading, setRatingLoading] = useState(true);

  // Regions (admin-managed). Code + name; stored on a game's `region` field.
  const [regions, setRegions] = useState([]);
  const [regionUsage, setRegionUsage] = useState({});
  const [regionLoading, setRegionLoading] = useState(true);

  const [search, setSearch] = useState("");

  // Form
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#3B82F6", description: "", system: "", aiHint: "", groupId: "", code: "" });
  const [saving, setSaving] = useState(false);

  // Merge
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [merging, setMerging] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isAch = activeTab === "achievement";
  const isGame = activeTab === "game";
  const isUnified = activeTab === "unified";
  const isPub = activeTab === "publishers";
  const isDev = activeTab === "developers";
  const isRating = activeTab === "ratings";
  const isGenre = activeTab === "genres";
  const isRegion = activeTab === "regions";
  const isMetadata = isPub || isDev || isRating;

  const tags = isAch ? achTags : isGame ? gameTags : isGenre ? genreItems : unifiedTags;
  const usage = isAch ? achUsage : isGame ? gameUsage : isUnified ? unifiedUsage : isPub ? pubUsage : isDev ? devUsage : isRating ? ratingUsage : isGenre ? genreUsage : isRegion ? regionUsage : {};
  const metaItems = isPub ? publishers : isDev ? developers : isGenre ? genreItems : isRegion ? regions : ratings;

  const fetchAchTags = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setAchLoading(true);
    try {
      const [t, u] = await Promise.all([
        axios.get(`${API}/master-tags`),
        axios.get(`${API}/master-tags/usage`),
      ]);
      setAchTags(t.data || []);
      setAchUsage(u.data || {});
    } catch (err) { console.error(err); }
    finally { if (!silent) setAchLoading(false); }
  }, []);

  const fetchGameTags = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setGameLoading(true);
    try {
      const [t, u] = await Promise.all([
        axios.get(`${API}/master-tags/game-tags`),
        axios.get(`${API}/master-tags/game-tags/usage`),
      ]);
      setGameTags(t.data || []);
      setGameUsage(u.data || {});
    } catch (err) { console.error(err); }
    finally { if (!silent) setGameLoading(false); }
  }, []);

  const fetchUnifiedTags = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setUnifiedLoading(true);
    try {
      const [t, u] = await Promise.all([
        axios.get(`${API}/master/unified-tags`),
        axios.get(`${API}/master-tags/unified-tags/usage`),
      ]);
      setUnifiedTags(t.data || []);
      setUnifiedUsage(u.data || {});
    } catch (err) { console.error(err); }
    finally { if (!silent) setUnifiedLoading(false); }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const [ag, ug] = await Promise.all([
        axios.get(`${API}/master-tags/groups`),
        axios.get(`${API}/master-tags/unified-groups`),
      ]);
      setAchGroups(ag.data || []);
      setUnifiedGroups(ug.data || []);
    } catch (err) { console.error(err); }
  }, []);

  const fetchPublishers = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setPubLoading(true);
    try {
      const [t, u] = await Promise.all([
        axios.get(`${API}/metadata-tags/publishers`),
        axios.get(`${API}/metadata-tags/publishers/usage`),
      ]);
      setPublishers(t.data || []);
      setPubUsage(u.data || {});
    } catch (err) { console.error(err); }
    finally { if (!silent) setPubLoading(false); }
  }, []);

  const fetchDevelopers = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setDevLoading(true);
    try {
      const [t, u] = await Promise.all([
        axios.get(`${API}/metadata-tags/developers`),
        axios.get(`${API}/metadata-tags/developers/usage`),
      ]);
      setDevelopers(t.data || []);
      setDevUsage(u.data || {});
    } catch (err) { console.error(err); }
    finally { if (!silent) setDevLoading(false); }
  }, []);

  const fetchRatings = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRatingLoading(true);
    try {
      const [t, u] = await Promise.all([
        axios.get(`${API}/metadata-tags/ratings`),
        axios.get(`${API}/metadata-tags/ratings/usage`),
      ]);
      setRatings(t.data || []);
      setRatingUsage(u.data || {});
    } catch (err) { console.error(err); }
    finally { if (!silent) setRatingLoading(false); }
  }, []);

  const fetchRegions = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRegionLoading(true);
    try {
      const [t, u] = await Promise.all([
        axios.get(`${API}/regions`),
        axios.get(`${API}/regions/usage`).catch(() => ({ data: {} })),
      ]);
      setRegions(t.data || []);
      setRegionUsage(u.data || {});
    } catch (err) { console.error(err); }
    finally { if (!silent) setRegionLoading(false); }
  }, []);

  // Genres share the same Tag UI but live under /master/genres
  const fetchGenres = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setGenreLoading(true);
    try {
      const [t, u] = await Promise.all([
        axios.get(`${API}/master/genres`),
        axios.get(`${API}/master/genres/usage`).catch(() => ({ data: {} })),
      ]);
      setGenreItems(t.data || []);
      setGenreUsage(u.data || {});
    } catch (err) { console.error(err); }
    finally { if (!silent) setGenreLoading(false); }
  }, []);

  useEffect(() => {
    fetchAchTags(); fetchGameTags(); fetchUnifiedTags();
    fetchPublishers(); fetchDevelopers(); fetchRatings();
    fetchGenres(); fetchRegions();
    fetchGroups();
  }, [fetchAchTags, fetchGameTags, fetchUnifiedTags, fetchPublishers, fetchDevelopers, fetchRatings, fetchGenres, fetchRegions, fetchGroups]);

  // Silent refresh — keeps current tags visible while data updates in the
  // background. Used by save/delete/merge/move handlers so the user doesn't
  // see the grid blank out and re-load on every edit.
  const refresh = () => {
    const opts = { silent: true };
    if (isAch) fetchAchTags(opts);
    else if (isGame) fetchGameTags(opts);
    else if (isUnified) fetchUnifiedTags(opts);
    else if (isPub) fetchPublishers(opts);
    else if (isDev) fetchDevelopers(opts);
    else if (isRating) fetchRatings(opts);
    else if (isGenre) fetchGenres(opts);
    else if (isRegion) fetchRegions(opts);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (isMetadata) {
        const metaType = isPub ? "publishers" : isDev ? "developers" : "ratings";
        const payload = { name: form.name, description: form.description || "" };
        if (isRating && form.system) payload.system = form.system;
        if (editingTag) {
          await axios.put(`${API}/metadata-tags/${metaType}/${editingTag.id}`, payload);
        } else {
          await axios.post(`${API}/metadata-tags/${metaType}`, payload);
        }
      } else if (isRegion) {
        if (!form.code.trim()) { setSaving(false); return toast.error("Region code is required"); }
        const payload = { code: form.code.trim(), name: form.name.trim(), description: form.description || "" };
        if (editingTag) {
          await axios.put(`${API}/regions/${editingTag.id}`, payload);
        } else {
          await axios.post(`${API}/regions`, payload);
        }
      } else if (isGenre) {
        // Genres CRUD is query-param based (legacy from Configuration page).
        const params = new URLSearchParams({ name: form.name, color: form.color });
        if (editingTag) {
          await axios.put(`${API}/master/genres/${editingTag.id}?${params.toString()}`);
        } else {
          await axios.post(`${API}/master/genres?${params.toString()}`);
        }
      } else if (isUnified) {
        const params = new URLSearchParams({ name: form.name, color: form.color });
        if (form.description) params.append("description", form.description);
        let tagId = editingTag?.id;
        if (editingTag) {
          await axios.put(`${API}/master/unified-tags/${editingTag.id}?${params.toString()}`);
        } else {
          const res = await axios.post(`${API}/master/unified-tags?${params.toString()}`);
          tagId = res.data?.id;
        }
        // Assign group via the dedicated endpoint since the unified-tags
        // endpoint above doesn't accept groupId on its own.
        if (tagId) {
          await axios.put(`${API}/master-tags/unified-tags/${tagId}/group`, {
            groupId: form.groupId || null,
          });
        }
      } else {
        const baseUrl = isAch ? `${API}/master-tags` : `${API}/master-tags/game-tags`;
        const payload = { name: form.name, color: form.color, description: form.description || "" };
        if (isAch) {
          payload.aiHint = form.aiHint || "";
          // Game tags don't have groups; only achievement tags do.
          payload.groupId = form.groupId || null;
        }
        if (editingTag) {
          await axios.put(`${baseUrl}/${editingTag.id}`, payload);
        } else {
          await axios.post(baseUrl, payload);
        }
      }
      toast.success(editingTag ? "Updated" : "Created");
      setShowForm(false);
      setEditingTag(null);
      setForm({ name: "", color: "#3B82F6", description: "", system: "", aiHint: "", groupId: "", code: "" });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (isMetadata) {
        const metaType = isPub ? "publishers" : isDev ? "developers" : "ratings";
        await axios.delete(`${API}/metadata-tags/${metaType}/${deleteTarget.id}`);
      } else if (isRegion) {
        await axios.delete(`${API}/regions/${deleteTarget.id}`);
      } else if (isGenre) {
        await axios.delete(`${API}/master/genres/${deleteTarget.id}`);
      } else if (isUnified) {
        await axios.delete(`${API}/master/unified-tags/${deleteTarget.id}`);
      } else {
        const baseUrl = isAch ? `${API}/master-tags` : `${API}/master-tags/game-tags`;
        await axios.delete(`${baseUrl}/${deleteTarget.id}`);
      }
      toast.success(`"${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleMerge = async () => {
    if (!mergeTarget || selectedForMerge.length < 2) return;
    setMerging(true);
    try {
      const sourceIds = selectedForMerge.filter(id => id !== mergeTarget);
      const mergeUrl = isAch ? `${API}/master-tags/merge` : `${API}/master-tags/game-tags/merge`;
      const res = await axios.post(mergeUrl, { sourceTagIds: sourceIds, targetTagId: mergeTarget });
      toast.success(res.data?.message || "Tags merged");
      exitMerge();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to merge");
    } finally { setMerging(false); }
  };

  const toggleMergeSelection = (id) => {
    setSelectedForMerge(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const exitMerge = () => {
    setMergeMode(false);
    setSelectedForMerge([]);
    setMergeTarget(null);
    setShowMergeConfirm(false);
  };

  const openEdit = (tag) => {
    setEditingTag(tag);
    setForm({ name: tag.name, color: tag.color || "#3B82F6", description: tag.description || "", system: tag.system || "", aiHint: tag.aiHint || "", groupId: tag.groupId || "", code: tag.code || "" });
    setShowForm(true);
  };
  const openNew = () => {
    setEditingTag(null);
    setForm({ name: "", color: "#3B82F6", description: "", system: "", aiHint: "", groupId: "", code: "" });
    setShowForm(true);
  };

  // ─── Group management ─── //
  const supportsGroups = isAch || isUnified;
  const activeGroups = isAch ? achGroups : isUnified ? unifiedGroups : [];
  const groupsEndpoint = isAch ? "groups" : "unified-groups";

  const refreshGroups = async () => {
    await fetchGroups();
  };

  const handleGroupSave = async () => {
    const name = groupForm.name.trim();
    if (!name) return toast.error("Group name is required");
    try {
      if (groupForm.id) {
        await axios.put(`${API}/master-tags/${groupsEndpoint}/${groupForm.id}`, { name });
      } else {
        await axios.post(`${API}/master-tags/${groupsEndpoint}`, { name });
      }
      toast.success(groupForm.id ? "Group renamed" : "Group created");
      setGroupForm({ id: null, name: "" });
      refreshGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save group");
    }
  };

  const handleGroupDelete = async (group) => {
    try {
      await axios.delete(`${API}/master-tags/${groupsEndpoint}/${group.id}`);
      toast.success(`Group "${group.name}" deleted`);
      refreshGroups();
      refresh(); // tag list reflects ungrouped state
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete group");
    }
  };

  const moveGroup = async (index, dir) => {
    const next = [...activeGroups];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    try {
      await axios.post(`${API}/master-tags/${groupsEndpoint}/reorder`, {
        orderedIds: next.map(g => g.id),
      });
      refreshGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reorder");
    }
  };

  const handleBulkMove = async () => {
    if (selectedForMerge.length === 0) return;
    setMoving(true);
    try {
      const targetId = moveTargetGroupId === "__none__" ? null : moveTargetGroupId;
      await axios.post(`${API}/master-tags/${groupsEndpoint}/bulk-assign`, {
        tagIds: selectedForMerge,
        groupId: targetId,
      });
      const label = targetId
        ? activeGroups.find(g => g.id === targetId)?.name || "group"
        : "Ungrouped";
      toast.success(`Moved ${selectedForMerge.length} tag(s) to "${label}"`);
      setShowMoveDialog(false);
      exitMerge();
      refresh();
      refreshGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to move tags");
    } finally {
      setMoving(false);
    }
  };


  const loading = isAch ? achLoading : isGame ? gameLoading : isUnified ? unifiedLoading : isPub ? pubLoading : isDev ? devLoading : isGenre ? genreLoading : isRegion ? regionLoading : ratingLoading;
  const tabLabel = isAch ? "achievement" : isGame ? "game" : isUnified ? "unified" : isPub ? "publisher" : isDev ? "developer" : isGenre ? "genre" : isRegion ? "region" : "rating";
  const canMerge = isAch || isGame;
  // Selection mode is available whenever the current tab supports either
  // merge or move-to-group, so it also covers the Unified tab.
  const canSelect = canMerge || isUnified;
  const showColor = !isMetadata && !isRegion;
  // Simple (non-colored, single-item) tabs that use the metadata grid.
  const isSimple = isMetadata || isRegion;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/app/master-database")} data-testid="back-to-mdb">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">Tags & Metadata</h1>
          <p className="text-white/50 text-sm">
            Manage {tabLabel} {isSimple ? "entries" : "tags"}. {isSimple ? metaItems.length : tags.length} total.
          </p>
        </div>
        <div className="flex gap-2">
          {mergeMode ? (
            <>
              <Button size="sm" variant="outline" onClick={exitMerge}><X className="w-4 h-4 mr-1" /> Cancel</Button>
              {supportsGroups && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={selectedForMerge.length < 1}
                  onClick={() => { setMoveTargetGroupId("__none__"); setShowMoveDialog(true); }}
                  data-testid="bulk-move-group-btn"
                  className="border-purple-500/40 hover:bg-purple-500/15"
                >
                  <Layers className="w-4 h-4 mr-1" /> Move to Group ({selectedForMerge.length})
                </Button>
              )}
              {canMerge && (
                <Button size="sm" disabled={selectedForMerge.length < 2} onClick={() => setShowMergeConfirm(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-merge-btn">
                  <Merge className="w-4 h-4 mr-1" /> Merge ({selectedForMerge.length})
                </Button>
              )}
            </>
          ) : (
            <>
              {supportsGroups && (
                <Button size="sm" variant="outline" onClick={() => setShowGroupManager(true)} data-testid="manage-groups-btn">
                  <Layers className="w-4 h-4 mr-1" /> Groups ({activeGroups.length})
                </Button>
              )}
              {canSelect && (
                <Button size="sm" variant="outline" onClick={() => { exitMerge(); setMergeMode(true); }} data-testid="start-merge-btn">
                  {canMerge ? (<><Merge className="w-4 h-4 mr-1" /> Select</>) : (<><Check className="w-4 h-4 mr-1" /> Select</>)}
                </Button>
              )}
              <Button size="sm" onClick={openNew} className="bg-purple-600 hover:bg-purple-700" data-testid="add-tag-btn">
                <Plus className="w-4 h-4 mr-1" /> Add {isSimple ? "" : "Tag"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); exitMerge(); setSearch(""); }}>
        <TabsList className="bg-black/50 w-full grid grid-cols-4 sm:grid-cols-8 h-auto gap-0">
          <TabsTrigger value="achievement" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-1 text-xs sm:text-sm py-2" data-testid="tab-achievement-tags">
            <Trophy className="w-3 h-3 sm:w-4 sm:h-4" /> Achievement ({achTags.length})
          </TabsTrigger>
          <TabsTrigger value="game" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white flex items-center gap-1 text-xs sm:text-sm py-2" data-testid="tab-game-tags">
            <Gamepad2 className="w-3 h-3 sm:w-4 sm:h-4" /> Game ({gameTags.length})
          </TabsTrigger>
          <TabsTrigger value="unified" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white flex items-center gap-1 text-xs sm:text-sm py-2" data-testid="tab-unified-tags">
            <Globe className="w-3 h-3 sm:w-4 sm:h-4" /> Unified ({unifiedTags.length})
          </TabsTrigger>
          <TabsTrigger value="genres" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white flex items-center gap-1 text-xs sm:text-sm py-2" data-testid="tab-genres">
            <Tag className="w-3 h-3 sm:w-4 sm:h-4" /> Genre ({genreItems.length})
          </TabsTrigger>
          <TabsTrigger value="publishers" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white flex items-center gap-1 text-xs sm:text-sm py-2" data-testid="tab-publishers">
            <Building2 className="w-3 h-3 sm:w-4 sm:h-4" /> Publishers ({publishers.length})
          </TabsTrigger>
          <TabsTrigger value="developers" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white flex items-center gap-1 text-xs sm:text-sm py-2" data-testid="tab-developers">
            <Wrench className="w-3 h-3 sm:w-4 sm:h-4" /> Developers ({developers.length})
          </TabsTrigger>
          <TabsTrigger value="ratings" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white flex items-center gap-1 text-xs sm:text-sm py-2" data-testid="tab-ratings">
            <Shield className="w-3 h-3 sm:w-4 sm:h-4" /> Ratings ({ratings.length})
          </TabsTrigger>
          <TabsTrigger value="regions" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white flex items-center gap-1 text-xs sm:text-sm py-2" data-testid="tab-regions">
            <Flag className="w-3 h-3 sm:w-4 sm:h-4" /> Regions ({regions.length})
          </TabsTrigger>
        </TabsList>

        {/* Merge instructions */}
        {mergeMode && (
          <Card className="bg-amber-500/10 border-amber-500/30 mt-4">
            <CardContent className="py-3 px-4 text-amber-300 text-sm">
              Select 2+ tags to merge, then click &quot;Merge&quot;. Choose which tag to keep.
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${tabLabel}s...`} className="pl-10 bg-[#0A0A0A] border-white/10 text-white" data-testid="tag-search" />
        </div>

        {/* Color tag tabs */}
        {["achievement", "game", "unified"].map(tabVal => (
          <TabsContent key={tabVal} value={tabVal} className="mt-4">
            {(tabVal === "achievement" ? achLoading : tabVal === "game" ? gameLoading : unifiedLoading) ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-white/30" /></div>
            ) : (
              <TagGrid
                tags={tabVal === "achievement" ? achTags : tabVal === "game" ? gameTags : unifiedTags}
                usage={tabVal === "achievement" ? achUsage : tabVal === "game" ? gameUsage : unifiedUsage}
                search={search} mergeMode={mergeMode && canSelect} selectedForMerge={selectedForMerge}
                toggleMergeSelection={toggleMergeSelection} openEdit={openEdit} setDeleteTarget={setDeleteTarget}
                showDescription={tabVal === "unified"}
                groups={tabVal === "achievement" ? achGroups : tabVal === "unified" ? unifiedGroups : []}
              />
            )}
          </TabsContent>
        ))}

        {/* Metadata tabs */}
        {[
          { val: "publishers", items: publishers, loading: pubLoading, usg: pubUsage },
          { val: "developers", items: developers, loading: devLoading, usg: devUsage },
          { val: "ratings", items: ratings, loading: ratingLoading, usg: ratingUsage },
        ].map(({ val, items, loading: ld, usg }) => (
          <TabsContent key={val} value={val} className="mt-4">
            {ld ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-white/30" /></div>
            ) : (
              <MetadataGrid items={items} usage={usg} search={search} openEdit={openEdit} setDeleteTarget={setDeleteTarget} />
            )}
          </TabsContent>
        ))}

        {/* Genres tab — genres are visually identical to color tags so we
            reuse the TagGrid component (with no group sections). */}
        <TabsContent value="genres" className="mt-4">
          {genreLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-white/30" /></div>
          ) : (
            <TagGrid
              tags={genreItems}
              usage={genreUsage}
              search={search}
              mergeMode={false}
              selectedForMerge={[]}
              toggleMergeSelection={() => {}}
              openEdit={openEdit}
              setDeleteTarget={setDeleteTarget}
              showDescription={false}
              groups={[]}
            />
          )}
        </TabsContent>

        {/* Regions tab — admin-managed region options (code + name). */}
        <TabsContent value="regions" className="mt-4">
          {regionLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-white/30" /></div>
          ) : (
            <MetadataGrid items={regions} usage={regionUsage} search={search} openEdit={openEdit} setDeleteTarget={setDeleteTarget} />
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) { setShowForm(false); setEditingTag(null); } }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="tag-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingTag ? "Edit" : "New"} {tabLabel.charAt(0).toUpperCase() + tabLabel.slice(1)}</DialogTitle>
            <DialogDescription className="text-white/50">
              {editingTag ? "Update the details." : `Create a new ${tabLabel}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {isRegion && (
              <div>
                <label className="text-sm text-white/60 mb-1 block">Code</label>
                <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g., DE, NA, EU" maxLength={6} className="bg-zinc-900 border-white/10 font-mono" data-testid="region-code-input" />
                <p className="text-[11px] text-white/30 mt-1">Short code stored on each game (uppercase).</p>
              </div>
            )}
            <div>
              <label className="text-sm text-white/60 mb-1 block">Name</label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={isPub ? "e.g., Microsoft, Bandai Namco" : isDev ? "e.g., 343 Industries, FromSoftware" : isRating ? "e.g., M, T, E10+" : isRegion ? "e.g., Germany (DE)" : isAch ? "e.g., Missable, Multiplayer" : isGame ? "e.g., Favorite, Backlog" : "e.g., Backwards Compatible"} className="bg-zinc-900 border-white/10" data-testid="tag-name-input" />
            </div>
            <div>
              <label className="text-sm text-white/60 mb-1 block">Description (optional)</label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Shown on hover..." className="bg-zinc-900 border-white/10" data-testid="tag-description-input" />
            </div>
            {isRating && (
              <div>
                <label className="text-sm text-white/60 mb-1 block">Rating System</label>
                <Input value={form.system} onChange={e => setForm({ ...form, system: e.target.value })} placeholder="e.g., ESRB, PEGI, CERO" className="bg-zinc-900 border-white/10" data-testid="rating-system-input" />
              </div>
            )}
            {supportsGroups && (
              <div>
                <label className="text-sm text-white/60 mb-1 block">Group</label>
                <Select
                  value={form.groupId || "__none__"}
                  onValueChange={(v) => setForm({ ...form, groupId: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger className="bg-zinc-900 border-white/10" data-testid="tag-group-select">
                    <SelectValue placeholder="Ungrouped" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="__none__">Ungrouped</SelectItem>
                    {activeGroups.map(g => (
                      <SelectItem key={g.id} value={g.id} data-testid={`tag-group-option-${g.id}`}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {activeGroups.length === 0 && (
                  <p className="text-[11px] text-white/30 mt-1">No groups yet — create one from the &quot;Groups&quot; button above.</p>
                )}
              </div>
            )}
            {showColor && (
              <>
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_OPTIONS.map(c => (
                      <button key={c} onClick={() => setForm({ ...form, color: c })} className={`w-8 h-8 rounded-full transition-all ${form.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#0A0A0A] scale-110" : "hover:scale-110"}`} style={{ backgroundColor: c }} />
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-white/40">Custom:</span>
                    <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-8 h-6 rounded cursor-pointer" />
                    <span className="text-xs text-white/50 font-mono">{form.color}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-white/5">
                  <Tag className="w-4 h-4" style={{ color: form.color }} />
                  <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: `${form.color}20`, color: form.color, border: `1px solid ${form.color}40` }}>
                    {form.name || "Preview"}
                  </span>
                </div>
              </>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowForm(false); setEditingTag(null); }}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="save-tag-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete {isSimple ? tabLabel : "Tag"}</DialogTitle>
            <DialogDescription className="text-white/50">
              Delete &quot;{deleteTarget?.name}&quot;?
              {!isSimple && !isUnified && (usage[deleteTarget?.id] || 0) > 0 && (
                <span className="text-red-400"> Removed from {usage[deleteTarget?.id]} {isAch ? "achievement" : "game"}{usage[deleteTarget?.id] !== 1 ? "s" : ""}.</span>
              )}
              {isSimple && (usage[deleteTarget?.id] || 0) > 0 && (
                <span className="text-red-400"> Used by {usage[deleteTarget?.id]} game{usage[deleteTarget?.id] !== 1 ? "s" : ""}.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge Confirm */}
      <Dialog open={showMergeConfirm} onOpenChange={(open) => !open && setShowMergeConfirm(false)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge Tags</DialogTitle>
            <DialogDescription className="text-white/50">
              Select which tag to keep. The other {selectedForMerge.length - 1} tag(s) will be merged into it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {selectedForMerge.map(id => {
              const tag = tags.find(t => t.id === id);
              if (!tag) return null;
              return (
                <button key={id} onClick={() => setMergeTarget(id)} className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${mergeTarget === id ? "border-amber-500 bg-amber-500/15" : "border-white/10 bg-white/5 hover:border-white/20"}`} data-testid={`merge-target-${id}`}>
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: tag.color }} />
                  <span className="text-white font-medium text-sm">{tag.name}</span>
                  <span className="text-white/40 text-xs ml-auto">{usage[id] || 0} uses</span>
                  {mergeTarget === id && <Check className="w-4 h-4 text-amber-400" />}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="outline" onClick={() => setShowMergeConfirm(false)}>Cancel</Button>
            <Button onClick={handleMerge} disabled={!mergeTarget || merging} className="bg-amber-600 hover:bg-amber-700" data-testid="execute-merge-btn">
              {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Merge className="w-4 h-4 mr-1" /> Merge</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Move to Group */}
      <Dialog open={showMoveDialog} onOpenChange={(open) => !open && setShowMoveDialog(false)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="bulk-move-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-purple-400" />
              Move {selectedForMerge.length} Tag{selectedForMerge.length === 1 ? "" : "s"} to Group
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Choose a destination group. Tags can also be moved back to &quot;Ungrouped&quot;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-white/60 mb-1 block">Destination</label>
              <Select value={moveTargetGroupId} onValueChange={setMoveTargetGroupId}>
                <SelectTrigger className="bg-zinc-900 border-white/10" data-testid="bulk-move-group-select">
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="__none__">Ungrouped</SelectItem>
                  {activeGroups.map(g => (
                    <SelectItem key={g.id} value={g.id} data-testid={`bulk-move-target-${g.id}`}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeGroups.length === 0 && (
                <p className="text-[11px] text-white/30 mt-1">No groups exist yet. Create one from the &quot;Groups&quot; button first.</p>
              )}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowMoveDialog(false)}>Cancel</Button>
              <Button
                onClick={handleBulkMove}
                disabled={moving}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="execute-bulk-move-btn"
              >
                {moving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Layers className="w-4 h-4 mr-1" /> Move</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Manager */}
      <Dialog open={showGroupManager} onOpenChange={(open) => { if (!open) { setShowGroupManager(false); setGroupForm({ id: null, name: "" }); } }}>        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="group-manager-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-purple-400" />
              {isAch ? "Achievement" : "Unified"} Tag Groups
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Organize tags into custom groups. Groups only affect how tags are arranged in MDB inputs for admins.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={groupForm.name}
                onChange={e => setGroupForm({ ...groupForm, name: e.target.value })}
                placeholder={groupForm.id ? "Rename group..." : "New group name..."}
                className="bg-zinc-900 border-white/10"
                onKeyDown={(e) => { if (e.key === "Enter") handleGroupSave(); }}
                data-testid="group-name-input"
              />
              <Button onClick={handleGroupSave} className="bg-purple-600 hover:bg-purple-700" data-testid="save-group-btn">
                {groupForm.id ? "Rename" : "Add"}
              </Button>
              {groupForm.id && (
                <Button variant="outline" onClick={() => setGroupForm({ id: null, name: "" })}>Cancel</Button>
              )}
            </div>
            {activeGroups.length === 0 ? (
              <p className="text-center text-white/40 text-sm py-4">No groups yet. Add your first group above.</p>
            ) : (
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {activeGroups.map((g, idx) => (
                  <div key={g.id} className="flex items-center gap-2 p-2 rounded bg-white/[0.03] border border-white/5" data-testid={`group-row-${g.id}`}>
                    <span className="flex-1 text-sm text-white truncate">{g.name}</span>
                    <button onClick={() => moveGroup(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-white/10 disabled:opacity-30" title="Move up" data-testid={`group-move-up-${g.id}`}>
                      <ChevronUp className="w-3.5 h-3.5 text-white/50" />
                    </button>
                    <button onClick={() => moveGroup(idx, 1)} disabled={idx === activeGroups.length - 1} className="p-1 rounded hover:bg-white/10 disabled:opacity-30" title="Move down" data-testid={`group-move-down-${g.id}`}>
                      <ChevronDown className="w-3.5 h-3.5 text-white/50" />
                    </button>
                    <button onClick={() => setGroupForm({ id: g.id, name: g.name })} className="p-1 rounded hover:bg-white/10" title="Rename" data-testid={`group-edit-${g.id}`}>
                      <Pencil className="w-3.5 h-3.5 text-white/50" />
                    </button>
                    <button onClick={() => handleGroupDelete(g)} className="p-1 rounded hover:bg-red-500/20" title="Delete" data-testid={`group-delete-${g.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400/60" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-white/30">
              Deleting a group will leave its tags ungrouped (no tags are removed).
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
